import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  InputAdornment,
  Stack,
  Tooltip,
} from '@mui/material';
import { hexToRgba } from '../user/content/utils/colorUtils';
import { Delete as DeleteIcon, Edit as EditIcon, Search as SearchIcon, FilterList as FilterIcon, Visibility, VisibilityOff, Public as PublicIcon } from '@mui/icons-material';
import { useUsers, useDeleteUser, useUpdateUser, useChangeUserPassword, useBackfillSecurityLogCountries } from '../hooks/useAdmin';
import { useAvailablePlans } from '../hooks/useCommon';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import PageSection from '../components/PageSection';
import type { User } from '../types/admin';

// Country code to country name mapping
const COUNTRY_NAMES: { [key: string]: string } = {
  'US': 'United States', 'GB': 'United Kingdom', 'CA': 'Canada', 'AU': 'Australia', 'NZ': 'New Zealand',
  'FR': 'France', 'DE': 'Germany', 'IT': 'Italy', 'ES': 'Spain', 'PT': 'Portugal', 'NL': 'Netherlands',
  'BE': 'Belgium', 'CH': 'Switzerland', 'AT': 'Austria', 'SE': 'Sweden', 'NO': 'Norway', 'DK': 'Denmark',
  'FI': 'Finland', 'PL': 'Poland', 'CZ': 'Czech Republic', 'IE': 'Ireland', 'GR': 'Greece', 'RU': 'Russia',
  'JP': 'Japan', 'CN': 'China', 'KR': 'South Korea', 'IN': 'India', 'BR': 'Brazil', 'MX': 'Mexico',
  'AR': 'Argentina', 'CL': 'Chile', 'CO': 'Colombia', 'PE': 'Peru', 'VE': 'Venezuela', 'ZA': 'South Africa',
  'EG': 'Egypt', 'NG': 'Nigeria', 'KE': 'Kenya', 'MA': 'Morocco', 'AE': 'United Arab Emirates', 'SA': 'Saudi Arabia',
  'IL': 'Israel', 'TR': 'Turkey', 'TH': 'Thailand', 'VN': 'Vietnam', 'PH': 'Philippines', 'ID': 'Indonesia',
  'MY': 'Malaysia', 'SG': 'Singapore', 'HK': 'Hong Kong', 'TW': 'Taiwan', 'PK': 'Pakistan', 'BD': 'Bangladesh',
  'NF': 'Not Found',
};

// Helper function to get country name from code
const getCountryName = (countryCode: string | null | undefined): string => {
  if (!countryCode) return 'Unknown';
  return COUNTRY_NAMES[countryCode.toUpperCase()] || countryCode.toUpperCase();
};

// Flag component using flagcdn.com (same as LanguageSwitcher)
// Defined outside component to avoid HMR issues
const Flag = ({ countryCode, size = 20 }: { countryCode: string; size?: number }) => {
  const countryName = getCountryName(countryCode);
  
  return (
    <Tooltip title={countryName} arrow>
      <img
        src={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`}
        srcSet={`https://flagcdn.com/w80/${countryCode.toLowerCase()}.png 2x`}
        width={size}
        height={Math.round(size * 0.75)}
        alt={countryCode}
        style={{ borderRadius: 2, objectFit: 'cover', cursor: 'help' }}
      />
    </Tooltip>
  );
};

export default function UserManagement() {
  const { user } = useAuth();
  const { t } = useTranslation(['admin', 'common']);
  
  // All hooks must be called before any conditional returns
  const { data: usersResponse, isLoading, error, refetch } = useUsers();
  const { data: plansData } = useAvailablePlans();
  const deleteUserMutation = useDeleteUser();
  const updateUserMutation = useUpdateUser();
  const changePasswordMutation = useChangeUserPassword();
  const backfillCountriesMutation = useBackfillSecurityLogCountries();
  
  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <PageSection title={t('admin:userManagement.title')} hideTitleOnMobile>
        <Alert severity="error">
          {t('admin:userManagement.permissionDenied')}
        </Alert>
      </PageSection>
    );
  }
  
  // Handle API response structure: { message, count, users } or direct array
  const allUsers: User[] = usersResponse && typeof usersResponse === 'object' && 'users' in usersResponse
    ? (usersResponse as any).users || []
    : Array.isArray(usersResponse) 
      ? usersResponse 
      : [];

  // Create a map of plan_id to plan name
  const planMap = useMemo(() => {
    const map: Record<number, string> = {};
    if (plansData && Array.isArray(plansData)) {
      plansData.forEach((plan: any) => {
        if (plan.id && plan.name) {
          map[plan.id] = plan.name;
        }
      });
    }
    return map;
  }, [plansData]);

  // Helper function to get plan name
  const getPlanName = (planId?: number) => {
    if (!planId) return t('admin:userManagement.noPlan');
    return planMap[planId] || `Plan ${planId}`;
  };

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [verifiedFilter, setVerifiedFilter] = useState<string>('all');

  // Filter and sort users based on search and filters
  const filteredUsers = useMemo(() => {
    const filtered = allUsers.filter((userItem) => {
      // Search filter (name, email)
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          userItem.email?.toLowerCase().includes(searchLower) ||
          userItem.first_name?.toLowerCase().includes(searchLower) ||
          userItem.last_name?.toLowerCase().includes(searchLower) ||
          `${userItem.first_name} ${userItem.last_name}`.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Role filter
      if (roleFilter !== 'all' && userItem.role !== roleFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'active' && !userItem.is_active) return false;
        if (statusFilter === 'inactive' && userItem.is_active) return false;
      }

      // Verified filter
      if (verifiedFilter !== 'all') {
        if (verifiedFilter === 'verified' && !userItem.is_verified) return false;
        if (verifiedFilter === 'unverified' && userItem.is_verified) return false;
      }

      return true;
    });
    
    // Sort by ID from smallest to largest (ascending)
    return filtered.sort((a, b) => a.id - b.id);
  }, [allUsers, searchTerm, roleFilter, statusFilter, verifiedFilter]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'client',
    is_active: true,
  });
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleDeleteUser = (userItem: User) => {
    setUserToDelete(userItem);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (userToDelete) {
      try {
        await deleteUserMutation.mutateAsync(userToDelete.id.toString());
        setDeleteDialogOpen(false);
        setUserToDelete(null);
        refetch();
      } catch (error) {
        // Error handled by mutation hook
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleEditUser = (userItem: User) => {
    setEditingUser(userItem);
    setEditFormData({
      first_name: userItem.first_name || '',
      last_name: userItem.last_name || '',
      email: userItem.email || '',
      role: userItem.role || 'client',
      is_active: userItem.is_active !== undefined ? userItem.is_active : true,
    });
    setNewPassword(''); // Reset password field
    setShowPassword(false);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    try {
      // Update user profile
      await updateUserMutation.mutateAsync({
        id: editingUser.id.toString(),
        data: editFormData,
      });
      
      // Change password if provided
      if (newPassword && newPassword.trim().length > 0) {
        if (newPassword.length < 8) {
          alert(t('admin:userManagement.passwordMinLength'));
          return;
        }
        await changePasswordMutation.mutateAsync({
          id: editingUser.id.toString(),
          new_password: newPassword,
        });
      }
      
      setEditDialogOpen(false);
      setEditingUser(null);
      setNewPassword('');
      refetch();
    } catch (error) {
      // Error handled by mutation hook
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
    setNewPassword('');
    setShowPassword(false);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'agency':
        return 'success';
      default:
        return 'primary';
    }
  };

  // Liquid glass effect styles for chips
  const getGlassChipStyles = (color: string) => {
    const colorMap: Record<string, { rgb: string; text: string }> = {
      error: { rgb: '239, 68, 68', text: '#c62828' },
      success: { rgb: '16, 185, 129', text: '#2e7d32' },
      warning: { rgb: '245, 158, 11', text: '#f57c00' },
      primary: { rgb: '108, 99, 255', text: '#7b1fa2' },
      default: { rgb: '156, 163, 175', text: '#616161' },
    };
    
    const colorInfo = colorMap[color] || colorMap.primary;
    
    return {
      background: `rgba(${colorInfo.rgb}, 0.1)`,
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      border: `1px solid rgba(${colorInfo.rgb}, 0.2)`,
      color: colorInfo.text,
      fontWeight: 500,
      borderRadius: 2,
    };
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
    setVerifiedFilter('all');
  };

  const handleBackfillCountries = async () => {
    if (window.confirm(t('admin:userManagement.backfillConfirm'))) {
      try {
        await backfillCountriesMutation.mutateAsync();
      } catch (error) {
        // Error handled by mutation hook
      }
    }
  };

  return (
    <PageSection
      title={t('admin:userManagement.title')}
      subtitle={t('admin:userManagement.subtitle')}
      loading={isLoading}
      loadingText={t('admin:userManagement.loading')}
      hideTitleOnMobile
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('admin:userManagement.loadError')}
        </Alert>
      )}

      {/* Filters Section */}
      <Box sx={{ mb: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2} 
          alignItems="center" 
          sx={{ 
            width: '100%', 
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <TextField
            placeholder={t('admin:userManagement.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 250 }}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 180, flexShrink: 0 }}>
            <InputLabel>{t('admin:userManagement.role')}</InputLabel>
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              label={t('admin:userManagement.role')}
            >
              <MenuItem value="all">{t('admin:userManagement.allRoles')}</MenuItem>
              <MenuItem value="client">{t('admin:userManagement.roles.client')}</MenuItem>
              <MenuItem value="agency">{t('admin:userManagement.roles.agency')}</MenuItem>
              <MenuItem value="admin">{t('admin:userManagement.roles.admin')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180, flexShrink: 0 }}>
            <InputLabel>{t('admin:userManagement.status')}</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label={t('admin:userManagement.status')}
            >
              <MenuItem value="all">{t('admin:userManagement.allStatus')}</MenuItem>
              <MenuItem value="active">{t('admin:userManagement.statuses.active')}</MenuItem>
              <MenuItem value="inactive">{t('admin:userManagement.statuses.inactive')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180, flexShrink: 0 }}>
            <InputLabel>{t('admin:userManagement.verified')}</InputLabel>
            <Select
              value={verifiedFilter}
              onChange={(e) => setVerifiedFilter(e.target.value)}
              label={t('admin:userManagement.verified')}
            >
              <MenuItem value="all">{t('admin:userManagement.all')}</MenuItem>
              <MenuItem value="verified">{t('admin:userManagement.verifiedStatus.verified')}</MenuItem>
              <MenuItem value="unverified">{t('admin:userManagement.verifiedStatus.unverified')}</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterIcon />}
            onClick={clearFilters}
            disabled={!searchTerm && roleFilter === 'all' && statusFilter === 'all' && verifiedFilter === 'all'}
            sx={{ 
              flexShrink: 0,
              height: '40px', // Match the height of small TextField/Select
              borderColor: '#6C63FF',
              color: '#6C63FF',
              '&:hover': {
                borderColor: '#5a52ee',
                backgroundColor: 'rgba(108, 99, 255, 0.04)',
              },
              '&:disabled': {
                borderColor: 'rgba(0, 0, 0, 0.26)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
            }}
          >
            {t('admin:userManagement.clearFilters')}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={backfillCountriesMutation.isPending ? <CircularProgress size={16} /> : <PublicIcon />}
            onClick={handleBackfillCountries}
            disabled={backfillCountriesMutation.isPending}
            sx={{ 
              flexShrink: 0,
              height: '40px', // Match the height of small TextField/Select
              background: 'linear-gradient(135deg, #6C63FF 0%, #FF6CAB 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a52ee 0%, #e55a8a 100%)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
            }}
          >
            {backfillCountriesMutation.isPending ? t('admin:userManagement.backfilling') : t('admin:userManagement.backfillCountries')}
          </Button>
        </Stack>
        {filteredUsers.length !== allUsers.length && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('admin:userManagement.showing', { count: filteredUsers.length, total: allUsers.length })}
          </Typography>
        )}
      </Box>

      {filteredUsers.length === 0 && !isLoading ? (
        <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {allUsers.length === 0 ? t('admin:userManagement.noUsers') : t('admin:userManagement.noUsersMatch')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: hexToRgba('#6C63FF', 0.15) }}>
                <TableCell><strong>{t('admin:userManagement.columns.id')}</strong></TableCell>
                <TableCell><strong>{t('admin:userManagement.columns.email')}</strong></TableCell>
                <TableCell><strong>{t('admin:userManagement.columns.name')}</strong></TableCell>
                <TableCell><strong>{t('admin:userManagement.columns.role')}</strong></TableCell>
                <TableCell><strong>{t('admin:userManagement.columns.country')}</strong></TableCell>
                <TableCell><strong>{t('admin:userManagement.columns.plan')}</strong></TableCell>
                <TableCell><strong>{t('admin:userManagement.columns.status')}</strong></TableCell>
                <TableCell><strong>{t('admin:userManagement.columns.verified')}</strong></TableCell>
                <TableCell><strong>{t('admin:userManagement.columns.created')}</strong></TableCell>
                <TableCell align="right"><strong>{t('admin:userManagement.columns.actions')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((userItem) => (
                <TableRow key={userItem.id} hover>
                  <TableCell>{userItem.id}</TableCell>
                  <TableCell>{userItem.email}</TableCell>
                  <TableCell>
                    {userItem.first_name} {userItem.last_name}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={userItem.role}
                      size="small"
                      sx={getGlassChipStyles(getRoleColor(userItem.role))}
                    />
                  </TableCell>
                  <TableCell>
                    {userItem.country ? (
                      userItem.country === 'NF' ? (
                        <Chip
                          label="NF"
                          size="small"
                          sx={getGlassChipStyles('default')}
                        />
                      ) : (
                        <Flag countryCode={userItem.country} size={20} />
                      )
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {getPlanName(userItem.plan_id)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={userItem.is_active ? t('admin:userManagement.statuses.active') : t('admin:userManagement.statuses.inactive')}
                      size="small"
                      sx={getGlassChipStyles(userItem.is_active ? 'success' : 'default')}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={userItem.is_verified ? t('admin:userManagement.verifiedStatus.verified') : t('admin:userManagement.verifiedStatus.unverified')}
                      size="small"
                      sx={getGlassChipStyles(userItem.is_verified ? 'success' : 'warning')}
                    />
                  </TableCell>
                  <TableCell>{formatDate(userItem.created_at)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEditUser(userItem)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteUser(userItem)}
                      color="error"
                      disabled={userItem.id === user?.id}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit User Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCancelEdit} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1.5,
            px: 2.5,
            background: 'linear-gradient(135deg, #6C63FF 0%, #FF6CAB 100%)',
            color: 'white',
            fontSize: '0.95rem',
            fontWeight: 600,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
            }}
          >
            ✏️
          </Box>
          {t('admin:userManagement.editUser')}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2, pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label={t('admin:userManagement.firstName')}
              value={editFormData.first_name}
              onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('admin:userManagement.lastName')}
              value={editFormData.last_name}
              onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('common:email')}
              type="email"
              value={editFormData.email}
              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>{t('admin:userManagement.role')}</InputLabel>
              <Select
                value={editFormData.role}
                onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                label={t('admin:userManagement.role')}
                disabled={editingUser?.id === user?.id}
              >
                <MenuItem value="client">{t('admin:userManagement.roles.client')}</MenuItem>
                <MenuItem value="agency">{t('admin:userManagement.roles.agency')}</MenuItem>
                <MenuItem value="admin">{t('admin:userManagement.roles.admin')}</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.is_active}
                  onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                  disabled={editingUser?.id === user?.id}
                />
              }
              label={t('admin:userManagement.statuses.active')}
            />
            <TextField
              label={t('admin:userManagement.newPassword')}
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              helperText={t('admin:userManagement.passwordHelper')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button 
            onClick={handleCancelEdit}
            sx={{
              color: '#6C63FF',
              '&:hover': {
                backgroundColor: 'rgba(108, 99, 255, 0.04)',
              },
            }}
          >
            {t('common:cancel')}
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            disabled={updateUserMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #6C63FF 0%, #FF6CAB 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a52ee 0%, #e55a8a 100%)',
              },
            }}
          >
            {updateUserMutation.isPending ? <CircularProgress size={20} /> : t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('admin:userManagement.deleteUser')}
        itemType="user"
        description={t('admin:userManagement.deleteConfirm', { email: userToDelete?.email })}
        warningMessage={t('admin:userManagement.deleteWarning')}
        isDeleting={deleteUserMutation.isPending}
        confirmButtonText={t('common:delete')}
        cancelButtonText={t('common:cancel')}
      />
    </PageSection>
  );
}

